import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {SearchInput} from '@/components/ui/search-input'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FichaDatos, type BloqueFicha} from '@/components/ui/ficha-datos'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {NoAccessState} from '@/components/no-access-state'
import {CampoEnLinea, CorreoEditor, DocumentoEditor} from '@/components/admin-usuarios/campos-en-linea'
import {AgregarMembresiaDialog, MembresiasSeccion} from '@/components/admin-usuarios/membresias'
import {VerificacionesTab} from '@/components/admin-usuarios/verificaciones'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useTableState} from '@/hooks/common/use-table-state'
import {USUARIOS_PAGE_SIZE, useUsuarios, type FiltroUsuarios, type Usuario} from '@/hooks/inci/consola/use-usuarios'

const formatoFecha = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

/** Sin correo no hay a dónde mandar la recuperación, y sin verificar el reset no sale (U17: nunca solo color). */
type EstadoCorreo = 'sin_correo' | 'sin_verificar' | 'verificado'

function estadoCorreo(usuario: Usuario): EstadoCorreo {
  if (!usuario.email) return 'sin_correo'
  return usuario.email_verified ? 'verificado' : 'sin_verificar'
}

const TONO_CORREO: Record<EstadoCorreo, 'green' | 'amber' | 'neutral'> = {
  verificado: 'green',
  sin_verificar: 'amber',
  sin_correo: 'neutral',
}

function ChipCorreo({usuario}: {usuario: Usuario}) {
  const {t} = useTranslation()
  const estado = estadoCorreo(usuario)
  return (
    <Tag tone={TONO_CORREO[estado]} data-testid={`usuario-correo-${estado}`}>
      {t(`usuarios.correo.${estado}`)}
    </Tag>
  )
}

/** La fila abre esta ficha y no una columna de botones: la tabla no carga actos (U21/R2). */
function UsuarioSheet({
  usuario,
  onClose,
  onAgregarMembresia,
}: {
  usuario: Usuario | null
  onClose: () => void
  onAgregarMembresia: (usuario: Usuario) => void
}) {
  const {t} = useTranslation()
  if (!usuario) return null

  const bloques: BloqueFicha[] = [
    {
      clave: 'cuenta',
      titulo: t('usuarios.ficha.cuenta'),
      testId: 'usuario-ficha-cuenta',
      filas: [
        {
          clave: 'documento',
          etiqueta: t('usuarios.ficha.documento'),
          valor: (
            <CampoEnLinea
              testId="usuario-documento"
              editarLabel={t('usuarios.ficha.editarDocumento')}
              valor={
                <span className="font-mono">
                  {usuario.tipo_documento} {usuario.cedula}
                </span>
              }
              editor={(cerrar) => <DocumentoEditor usuario={usuario} onCerrar={cerrar} />}
            />
          ),
        },
        {
          clave: 'email',
          etiqueta: t('usuarios.ficha.email'),
          valor: (
            <CampoEnLinea
              testId="usuario-correo"
              editarLabel={t('usuarios.ficha.editarCorreo')}
              valor={
                <span className="flex flex-wrap items-center gap-2">
                  <ChipCorreo usuario={usuario} />
                  {usuario.email ?? t('usuarios.ficha.vacio')}
                </span>
              }
              editor={(cerrar) => <CorreoEditor usuario={usuario} onCerrar={cerrar} />}
            />
          ),
        },
        {
          clave: 'phone',
          etiqueta: t('usuarios.ficha.phone'),
          valor: usuario.phone ?? t('usuarios.ficha.vacio'),
        },
        {
          clave: 'acceso',
          etiqueta: t('usuarios.ficha.acceso'),
          valor: <Tag tone="neutral">{t(`usuarios.acceso.${usuario.acceso}`)}</Tag>,
        },
        {
          clave: 'created_at',
          etiqueta: t('usuarios.ficha.creada'),
          valor: formatoFecha.format(new Date(usuario.created_at)),
        },
      ],
    },
  ]

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="usuario-sheet">
        <DialogHeader>
          <DialogTitle>
            {usuario.name} {usuario.last_name}
          </DialogTitle>
        </DialogHeader>

        <FichaDatos bloques={bloques} testId="usuario-ficha" />

        <MembresiasSeccion usuario={usuario} />

        <DialogFooter>
          <Button size="sm" data-testid="usuario-membresia-open" onClick={() => onAgregarMembresia(usuario)}>
            {t('usuarios.membresias.agregar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Cuentas y no empresas (KAI-158, KAI-230): el correo es opcional al registrarse y desde aquí un admin
 * lo fija para que la persona recupere el acceso por el flujo normal. */
export function UsuariosPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const table = useTableState('created_at')
  const [filtro, setFiltro] = useState<FiltroUsuarios>('todos')
  const [detalleId, setDetalleId] = useState<string | null>(null)
  const [agregandoId, setAgregandoId] = useState<string | null>(null)
  const [tab, setTab] = useState<'cuentas' | 'verificacion'>('cuentas')
  const {data, isLoading} = useUsuarios({page: table.page, search: table.search, filtro}, isSystemUser)

  if (!isSystemUser) return <NoAccessState />

  // Contra la página viva y no una copia: la ficha refleja las correcciones sin reabrirla.
  const filas = data?.items ?? []
  const detalle = filas.find((u) => u.id === detalleId) ?? null
  const agregando = filas.find((u) => u.id === agregandoId) ?? null

  const opciones: OpcionEstado<FiltroUsuarios>[] = [
    {valor: 'todos', etiqueta: t('usuarios.filtro.todos')},
    {valor: 'sin_correo', etiqueta: t('usuarios.filtro.sin_correo')},
    {valor: 'sin_verificar', etiqueta: t('usuarios.filtro.sin_verificar')},
    {valor: 'sin_acceso', etiqueta: t('usuarios.filtro.sin_acceso')},
  ]

  const columns: Column<Usuario>[] = [
    {
      key: 'persona',
      header: t('usuarios.table.persona'),
      cell: (u) => (
        <CeldaEstado nombre={`${u.name} ${u.last_name}`} meta={<span className="font-mono">{u.cedula}</span>} />
      ),
    },
    {
      key: 'contacto',
      header: t('usuarios.table.contacto'),
      width: 'w-64',
      cell: (u) => (
        <CeldaEstado chip={<ChipCorreo usuario={u} />} nombre={u.email ?? undefined} meta={u.phone ?? undefined} />
      ),
    },
    {
      key: 'acceso',
      header: t('usuarios.table.acceso'),
      width: 'w-36',
      cell: (u) => <CeldaEstado chip={<Tag tone="neutral">{t(`usuarios.acceso.${u.acceso}`)}</Tag>} />,
    },
    {
      key: 'empresas',
      header: t('usuarios.table.empresas'),
      width: 'w-24',
      className: 'text-right',
      cell: (u) => <span className="font-mono text-sm">{u.membresias.length}</span>,
    },
  ]

  return (
    <div className="space-y-5" data-testid="usuarios-page">
      <PageHeading
        title={t('usuarios.title')}
        ayuda={
          <HelpIcon label={t('usuarios.ayudaLabel')} testId="usuarios-ayuda">
            {t('usuarios.ayuda')}
          </HelpIcon>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'cuentas' | 'verificacion')}>
        <TabsList data-testid="usuarios-tabs">
          <TabsTrigger value="cuentas" data-testid="usuarios-tab-cuentas">
            {t('usuarios.tabs.cuentas')}
          </TabsTrigger>
          <TabsTrigger value="verificacion" data-testid="usuarios-tab-verificacion">
            {t('usuarios.tabs.verificacion')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="verificacion" className="pt-4">
          <VerificacionesTab />
        </TabsContent>
        <TabsContent value="cuentas" className="pt-4">
          {isLoading && !data ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ListCard
              toolbar={
                <>
                  <SearchInput
                    value={table.searchInput}
                    onChange={table.setSearchInput}
                    placeholder={t('usuarios.searchPlaceholder')}
                    data-testid="usuarios-search"
                  />
                  <FiltrosDeEstado
                    opciones={opciones}
                    valor={filtro}
                    onValor={(siguiente) => {
                      setFiltro(siguiente)
                      table.setPage(1)
                    }}
                    testIdPrefijo="usuarios-filtro"
                  />
                </>
              }
              footer={
                !!data?.total &&
                filas.length > 0 && (
                  <PaginationBar
                    page={table.page}
                    pageSize={USUARIOS_PAGE_SIZE}
                    total={data.total}
                    onPageChange={table.setPage}
                  />
                )
              }>
              {data && filas.length === 0 ? (
                <ListEmptyState
                  page
                  title={table.search || filtro !== 'todos' ? t('usuarios.noResults') : t('usuarios.empty')}
                />
              ) : (
                <DataTable columns={columns} rows={filas} rowKey={(u) => u.id} onRowClick={(u) => setDetalleId(u.id)} />
              )}
            </ListCard>
          )}
        </TabsContent>
      </Tabs>

      <UsuarioSheet
        usuario={detalle}
        onClose={() => setDetalleId(null)}
        onAgregarMembresia={(u) => setAgregandoId(u.id)}
      />
      <AgregarMembresiaDialog usuario={agregando} onClose={() => setAgregandoId(null)} />
    </div>
  )
}
